import { DisplayField, DisplayRow } from '../../ui/DisplayRow'
import { SegmentedControl } from '../../ui/SegmentedControl'
import {
  READER_FACES,
  READER_LEADS,
  READER_MEASURES,
  READER_STEPS,
  READER_TINTS,
  type ReaderSettings,
} from './reader-settings'

/**
 * How the book is set for the person reading it — the size, the leading and the
 * measure.
 *
 * The band is `DisplayRow`, which a song's own options open onto too. What is
 * here is the part that belongs to a BOOK: a face it may be set in, a paper it
 * may be read on, a column width. None of those is a fact about a chord sheet.
 */
export function ReaderDisplay({
  open,
  settings,
  onChange,
  onClose,
}: {
  open: boolean
  settings: ReaderSettings
  onChange: (patch: Partial<ReaderSettings>) => void
  /** See `DisplayRow` — drawn only where nothing else offers the way out. */
  onClose?: () => void
}) {
  return (
    <DisplayRow open={open} onClose={onClose}>
      <DisplayField label="Text size">
        <SegmentedControl
          size="sm"
          ariaLabel="Text size"
          value={String(settings.text)}
          onChange={(v) =>
            onChange({ text: Number(v) as ReaderSettings['text'] })
          }
          options={READER_STEPS.map((s) => ({
            value: String(s),
            /* The steps are named by how they read, not by a number nobody can
               picture — and the label is set at the step it names, so the
               control shows the answer rather than describing it. */
            label: <span data-step={s}>A</span>,
            ariaLabel: `Step ${s} of ${READER_STEPS.length}`,
          }))}
        />
      </DisplayField>

      <DisplayField label="Line spacing">
        <SegmentedControl
          size="sm"
          ariaLabel="Line spacing"
          value={settings.lead}
          onChange={(lead) => onChange({ lead })}
          options={READER_LEADS.map((l) => ({
            value: l,
            label:
              l === 'normal' ? 'Normal' : l === 'tight' ? 'Tight' : 'Loose',
          }))}
        />
      </DisplayField>

      <DisplayField label="Typeface">
        <SegmentedControl
          size="sm"
          ariaLabel="Typeface"
          value={settings.face}
          onChange={(face) => onChange({ face })}
          options={READER_FACES.map((f) => ({
            value: f,
            /* "As set" rather than a face name: what the book is set in is the
               book's to say and may change, so the option names the deference
               rather than whatever it happens to defer to today. */
            label: f === 'book' ? 'As set' : f === 'sans' ? 'Sans' : 'Serif',
          }))}
        />
      </DisplayField>

      <DisplayField label="Paper">
        <SegmentedControl
          size="sm"
          ariaLabel="Paper"
          value={settings.tint}
          onChange={(tint) => onChange({ tint })}
          options={READER_TINTS.map((t) => ({
            value: t,
            label: t === 'page' ? 'Default' : t === 'sepia' ? 'Sepia' : 'Night',
          }))}
        />
      </DisplayField>

      <DisplayField label="Column width">
        <SegmentedControl
          size="sm"
          ariaLabel="Column width"
          value={settings.measure}
          onChange={(measure) => onChange({ measure })}
          options={READER_MEASURES.map((m) => ({
            value: m,
            label:
              m === 'normal' ? 'Normal' : m === 'narrow' ? 'Narrow' : 'Wide',
          }))}
        />
      </DisplayField>
    </DisplayRow>
  )
}
